import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import { AdminHistoryEvent, AdminUser } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { relativeTimeLabel } from "../lib/relativeTime";
import { ConfirmDialog } from "./ConfirmDialog";

type DrawerTab = "overview" | "activity" | "devices" | "access" | "moderation";
type HistoryType = AdminHistoryEvent["type"];
type ModerationAction = "suspend" | "reinstate" | "ban" | "unban" | null;

const HISTORY_TYPES: readonly HistoryType[] = [
  "account",
  "invite",
  "report",
  "feedback",
  "grant",
  "notice",
  "admin",
];

function titleCase(value: string): string {
  return value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(epochSeconds: number | null | undefined): string {
  return epochSeconds == null ? "—" : new Date(epochSeconds * 1000).toLocaleString();
}

function historySummary(event: AdminHistoryEvent): string {
  switch (event.type) {
    case "account":
      return `Account ${event.event}`;
    case "invite":
      return `Invite ${event.code} — ${event.outcome}${event.reason ? ` (${event.reason})` : ""}`;
    case "report":
      return `Report ${event.targetType}:${event.targetId} · ${event.reason} (${event.status})`;
    case "feedback":
      return `Feedback: ${event.excerpt}`;
    case "grant":
      return `Grant ${event.kind}:${event.ref}${
        event.expiresAt ? ` (expires ${new Date(event.expiresAt * 1000).toLocaleDateString()})` : ""
      }`;
    case "notice":
      return `Notice (${event.kind}): ${event.excerpt}`;
    case "admin":
      return `${event.action}${event.targetRef ? ` — ${event.targetRef}` : ""}${
        event.reason ? ` (${event.reason})` : ""
      }`;
  }
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
      {children}
    </Typography>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(90px, 0.8fr) minmax(0, 1.2fr)", sm: "130px minmax(0, 1fr)" },
        gap: 1.5,
        alignItems: "baseline",
        py: 0.65,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {children}
      </Typography>
    </Box>
  );
}

function CountCard({
  label,
  count,
  loading,
  error,
}: {
  label: string;
  count: number;
  loading: boolean;
  error: boolean;
}) {
  return (
    <Box
      sx={{
        flex: "1 1 135px",
        minWidth: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        p: 1.5,
      }}
    >
      {loading ? (
        <CircularProgress size={20} aria-label={`Loading ${label.toLowerCase()}`} />
      ) : (
        <Typography variant="h5" component="div" color={error ? "text.disabled" : "text.primary"}>
          {error ? "—" : count}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {error ? `${label} unavailable` : label}
      </Typography>
    </Box>
  );
}

function SectionLoading({ label }: { label: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
      <CircularProgress size={28} aria-label={label} />
    </Box>
  );
}

/**
 * Frontend-only Member CRM / 360° view (docs/384 idea #9). Each existing backend
 * read has its own TanStack query so identity, history, devices, grants, and
 * moderation can resolve independently. The base identity row comes from the
 * People roster; account creation is derived from the existing history feed.
 */
export function PersonDetailDrawer({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}) {
  const { client, token } = useAuth();
  const queryClient = useQueryClient();
  const enabled = open && !!user && !!client && !!token;
  const [tab, setTab] = useState<DrawerTab>("overview");
  const [historyType, setHistoryType] = useState<HistoryType | "all">("all");
  const [pendingAction, setPendingAction] = useState<ModerationAction>(null);
  const [reason, setReason] = useState("");
  const [takedownRef, setTakedownRef] = useState("");
  const [takedownReason, setTakedownReason] = useState("");

  useEffect(() => {
    setTab("overview");
    setHistoryType("all");
    setPendingAction(null);
    setReason("");
  }, [user?.id]);

  const grantsQuery = useQuery({
    queryKey: ["admin-grants", user?.id],
    queryFn: () => client!.adminGrants(token!, user!.id),
    enabled,
  });
  const devicesQuery = useQuery({
    queryKey: ["admin-user-devices", user?.id],
    queryFn: () => client!.adminUserDevices(token!, user!.id),
    enabled,
  });
  const historyQuery = useQuery({
    queryKey: ["admin-user-history", user?.id],
    queryFn: () => client!.adminUserHistory(token!, user!.id),
    enabled,
  });
  const moderationQuery = useQuery({
    queryKey: ["admin-moderation", user?.id],
    queryFn: () => client!.adminModeration(token!, user!.id),
    enabled,
  });

  const grants = grantsQuery.data ?? [];
  const devices = devicesQuery.data ?? [];
  const history = useMemo(
    () => [...(historyQuery.data ?? [])].sort((left, right) => right.at - left.at),
    [historyQuery.data],
  );
  const filteredHistory = useMemo(
    () => (historyType === "all" ? history : history.filter((event) => event.type === historyType)),
    [history, historyType],
  );
  const createdAt = history.find((event) => event.type === "account" && event.event === "created")?.at;
  const claimedAt =
    user?.claimedAt ?? history.find((event) => event.type === "account" && event.event === "claimed")?.at;
  const provisionedAt =
    user?.provisionedAt ?? history.find((event) => event.type === "account" && event.event === "provisioned")?.at;
  const moderation = moderationQuery.data ?? null;
  const moderationStatus = moderation?.status ?? "active";
  const now = Date.now();

  const invalidateModeration = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-moderation", user?.id] });
  const openAction = (action: Exclude<ModerationAction, null>) => {
    setReason("");
    setPendingAction(action);
  };
  const suspendMutation = useMutation({
    mutationFn: () => client!.adminSuspend(token!, { userId: user!.id, reason: reason.trim() || undefined }),
    onSuccess: () => {
      invalidateModeration();
      setPendingAction(null);
    },
  });
  const reinstateMutation = useMutation({
    mutationFn: () => client!.adminReinstate(token!, user!.id),
    onSuccess: () => {
      invalidateModeration();
      setPendingAction(null);
    },
  });
  const banMutation = useMutation({
    mutationFn: () => client!.adminBan(token!, user!.id, reason.trim() || undefined),
    onSuccess: () => {
      invalidateModeration();
      setPendingAction(null);
    },
  });
  const unbanMutation = useMutation({
    mutationFn: () => client!.adminUnban(token!, user!.id),
    onSuccess: () => {
      invalidateModeration();
      setPendingAction(null);
    },
  });
  const actionMutation = {
    suspend: suspendMutation,
    reinstate: reinstateMutation,
    ban: banMutation,
    unban: unbanMutation,
  }[pendingAction ?? "suspend"];
  const takedownMutation = useMutation({
    mutationFn: () => client!.adminTakedownContent(token!, takedownRef.trim(), takedownReason.trim() || undefined),
    onSuccess: () => {
      setTakedownRef("");
      setTakedownReason("");
    },
  });

  const moderationChip = moderationQuery.isLoading ? (
    <CircularProgress size={18} aria-label="Loading moderation status" />
  ) : moderationQuery.isError ? (
    <Chip size="small" label="Status unavailable" />
  ) : (
    <Chip
      size="small"
      label={titleCase(moderationStatus)}
      color={
        moderationStatus === "banned"
          ? "error"
          : moderationStatus === "suspended" || moderationStatus === "warned"
            ? "warning"
            : "success"
      }
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 540 }, maxWidth: "100vw" } } }}
    >
      <Box sx={{ width: "100%", minWidth: 0, p: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
              {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Unnamed member"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {user?.email}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              <Chip size="small" variant="outlined" label={titleCase(user?.role ?? "viewer")} />
              {user?.deletedAt ? <Chip size="small" color="error" label="Removed" /> : null}
              {provisionedAt ? <Chip size="small" color="info" label="Provisioned" /> : null}
              {claimedAt ? (
                <Chip size="small" color="success" label="Claimed" />
              ) : provisionedAt ? (
                <Chip size="small" color="warning" label="Awaiting claim" />
              ) : null}
              {moderationChip}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close member profile">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Tabs
          value={tab}
          onChange={(_event, value: DrawerTab) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Member profile sections"
          sx={{ mb: 2.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="activity" label="Activity" />
          <Tab value="devices" label="Devices" />
          <Tab value="access" label="Access" />
          <Tab value="moderation" label="Moderation" />
        </Tabs>

        {tab === "overview" ? (
          <Stack role="tabpanel" spacing={2.5}>
            <Box>
              <SectionHeading>Identity</SectionHeading>
              <DetailRow label="Email">{user?.email ?? "—"}</DetailRow>
              <DetailRow label="Name">
                {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Not provided"}
              </DetailRow>
              <DetailRow label="Role">{titleCase(user?.role ?? "viewer")}</DetailRow>
              <DetailRow label="Member ID">{user?.id ?? "—"}</DetailRow>
              <DetailRow label="Created">
                {historyQuery.isLoading ? "Loading…" : historyQuery.isError ? "Unavailable" : formatDateTime(createdAt)}
              </DetailRow>
              <DetailRow label="Provisioned">{formatDateTime(provisionedAt)}</DetailRow>
              <DetailRow label="Claimed">
                {claimedAt ? formatDateTime(claimedAt) : provisionedAt ? "Awaiting first verified sign-in" : "—"}
              </DetailRow>
            </Box>

            <Box>
              <SectionHeading>At a glance</SectionHeading>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                <CountCard
                  label="Devices"
                  count={devices.length}
                  loading={devicesQuery.isLoading}
                  error={devicesQuery.isError}
                />
                <CountCard
                  label="Access grants"
                  count={grants.length}
                  loading={grantsQuery.isLoading}
                  error={grantsQuery.isError}
                />
                <CountCard
                  label="History events"
                  count={history.length}
                  loading={historyQuery.isLoading}
                  error={historyQuery.isError}
                />
              </Box>
            </Box>

            <Box>
              <SectionHeading>Moderation status</SectionHeading>
              {moderationQuery.isError ? (
                <Alert severity="error">{(moderationQuery.error as Error).message}</Alert>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                  {moderationChip}
                  {moderation?.reason ? (
                    <Typography variant="body2" color="text.secondary">
                      {moderation.reason}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Box>

            <Box>
              <SectionHeading>Admin private notes</SectionHeading>
              {/* TODO(docs/384 follow-up): persistence requires a worker-backed `user_notes`
                  field and contract/client support. Keep this read-only placeholder until
                  that data model exists; do not imply that typed notes would be saved. */}
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Private notes are not available yet. This section requires a backend <code>user_notes</code> field.
                </Typography>
              </Box>
            </Box>
          </Stack>
        ) : null}

        {tab === "activity" ? (
          <Box role="tabpanel">
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: 1,
                mb: 2,
              }}
            >
              <SectionHeading>Activity / history</SectionHeading>
              <TextField
                select
                size="small"
                label="Event type"
                value={historyType}
                onChange={(event) => setHistoryType(event.target.value as HistoryType | "all")}
                slotProps={{ select: { native: true } }}
                sx={{ minWidth: { sm: 165 } }}
              >
                <option value="all">All activity</option>
                {HISTORY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {titleCase(type)}
                  </option>
                ))}
              </TextField>
            </Box>
            {historyQuery.isError ? (
              <Alert severity="error">{(historyQuery.error as Error).message}</Alert>
            ) : historyQuery.isLoading ? (
              <SectionLoading label="Loading member activity" />
            ) : filteredHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {history.length === 0 ? "No activity recorded." : "No events match this type."}
              </Typography>
            ) : (
              <Box component="ol" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {filteredHistory.map((event, index) => (
                  <Box
                    component="li"
                    key={`${event.type}-${event.at}-${index}`}
                    sx={{ position: "relative", pl: 3, pb: index === filteredHistory.length - 1 ? 0 : 2.5 }}
                  >
                    {index !== filteredHistory.length - 1 ? (
                      <Box
                        aria-hidden
                        sx={{ position: "absolute", left: 6, top: 12, bottom: -4, borderLeft: "2px solid", borderColor: "divider" }}
                      />
                    ) : null}
                    <Box
                      aria-hidden
                      sx={{ position: "absolute", left: 1, top: 6, width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.main" }}
                    />
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 0.4 }}>
                      <Chip size="small" variant="outlined" label={titleCase(event.type)} />
                      <Tooltip title={new Date(event.at * 1000).toLocaleString()}>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {relativeTimeLabel(now, event.at * 1000)}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2">{historySummary(event)}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : null}

        {tab === "devices" ? (
          <Box role="tabpanel">
            <SectionHeading>Connected devices</SectionHeading>
            {devicesQuery.isError ? (
              <Alert severity="error">{(devicesQuery.error as Error).message}</Alert>
            ) : devicesQuery.isLoading ? (
              <SectionLoading label="Loading member devices" />
            ) : devices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No devices recorded.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {devices.map((device) => (
                  <Box key={device.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {titleCase(device.deviceType)}
                        {device.platform && device.platform.toLowerCase() !== device.deviceType.toLowerCase()
                          ? ` · ${titleCase(device.platform)}`
                          : ""}
                      </Typography>
                      {device.appVersion ? <Chip size="small" variant="outlined" label={`App ${device.appVersion}`} /> : null}
                    </Box>
                    <Tooltip title={new Date(device.lastSeenAt * 1000).toLocaleString()}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Last seen {relativeTimeLabel(now, device.lastSeenAt * 1000)}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      First seen {new Date(device.firstSeenAt * 1000).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        ) : null}

        {tab === "access" ? (
          <Box role="tabpanel">
            <SectionHeading>Access / catalog grants</SectionHeading>
            {user?.role === "admin" ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Admins can access all content regardless of explicit grants.
              </Alert>
            ) : null}
            {grantsQuery.isError ? (
              <Alert severity="error">{(grantsQuery.error as Error).message}</Alert>
            ) : grantsQuery.isLoading ? (
              <SectionLoading label="Loading member grants" />
            ) : grants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No catalog or pack grants.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {grants.map((grant) => {
                  const expired = grant.expiresAt != null && grant.expiresAt * 1000 <= now;
                  return (
                    <Box key={grant.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                        <Chip size="small" color={grant.kind === "catalog" ? "primary" : "default"} label={titleCase(grant.kind)} />
                        {expired ? <Chip size="small" color="warning" label="Expired" /> : null}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontFamily: "monospace", overflowWrap: "anywhere" }}>
                        {grant.ref}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Granted {relativeTimeLabel(now, grant.grantedAt * 1000)}
                        {grant.grantedBy ? ` by ${grant.grantedBy}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {grant.expiresAt ? `Expires ${formatDateTime(grant.expiresAt)}` : "Does not expire"}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        ) : null}

        {tab === "moderation" ? (
          <Box role="tabpanel">
            <SectionHeading>Account moderation</SectionHeading>
            {moderationQuery.isError ? (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {(moderationQuery.error as Error).message}
              </Alert>
            ) : moderationQuery.isLoading ? (
              <SectionLoading label="Loading moderation status" />
            ) : (
              <Stack spacing={1.25}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                  {moderationChip}
                  {moderation?.reason ? (
                    <Typography variant="body2" color="text.secondary">
                      {moderation.reason}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {moderationStatus === "banned" ? (
                    <Button size="small" variant="outlined" color="secondary" onClick={() => openAction("unban")}>
                      Unban
                    </Button>
                  ) : (
                    <>
                      {moderationStatus === "suspended" ? (
                        <Button size="small" variant="outlined" color="secondary" onClick={() => openAction("reinstate")}>
                          Reinstate
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" color="warning" onClick={() => openAction("suspend")}>
                          Suspend
                        </Button>
                      )}
                      <Button size="small" variant="outlined" color="error" onClick={() => openAction("ban")}>
                        Ban
                      </Button>
                    </>
                  )}
                </Box>
              </Stack>
            )}

            <Divider sx={{ my: 2.5 }} />
            <SectionHeading>Content takedown</SectionHeading>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
              Blocklists a pack or catalog reference. This action is independent of the open member.
            </Typography>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                label="Ref (for example, pack:my-pack-id)"
                value={takedownRef}
                onChange={(event) => setTakedownRef(event.target.value)}
              />
              <TextField
                size="small"
                label="Reason (optional)"
                value={takedownReason}
                onChange={(event) => setTakedownReason(event.target.value)}
              />
              {takedownMutation.isError ? (
                <Alert severity="error">{(takedownMutation.error as Error).message}</Alert>
              ) : null}
              {takedownMutation.isSuccess ? <Alert severity="success">Taken down.</Alert> : null}
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={!takedownRef.trim() || takedownMutation.isPending}
                onClick={() => takedownMutation.mutate()}
              >
                Take down
              </Button>
            </Stack>
          </Box>
        ) : null}
      </Box>

      <ConfirmDialog
        open={pendingAction !== null}
        title={
          pendingAction === "suspend"
            ? "Suspend this account?"
            : pendingAction === "ban"
              ? "Ban this account?"
              : pendingAction === "reinstate"
                ? "Reinstate this account?"
                : "Unban this account?"
        }
        body={
          pendingAction === "suspend" || pendingAction === "ban"
            ? ""
            : pendingAction === "reinstate"
              ? "Clears the suspension or warning and restores full access."
              : "Clears the ban and restores full access."
        }
        confirmLabel={
          pendingAction === "suspend"
            ? "Suspend"
            : pendingAction === "ban"
              ? "Ban"
              : pendingAction === "reinstate"
                ? "Reinstate"
                : "Unban"
        }
        danger={pendingAction === "suspend" || pendingAction === "ban"}
        busy={actionMutation.isPending}
        error={actionMutation.isError ? (actionMutation.error as Error).message : null}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => actionMutation.mutate()}
      >
        {pendingAction === "suspend" || pendingAction === "ban" ? (
          <TextField
            size="small"
            fullWidth
            label="Reason (optional)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            sx={{ mt: 1 }}
          />
        ) : null}
      </ConfirmDialog>
    </Drawer>
  );
}
